import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { apiService } from '../services/api.service';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { formatTermsVersion } from '../lib/termsVersion';

/**
 * Suivi de la collecte des régimes TVA.
 *
 * Sert une décision précise : quand peut-on activer BILLING_ENABLED ? Le
 * verrou `require_vat_regime` bloque en 403 toute action facturable d'une
 * organisation sans régime déclaré. L'activer alors que des organisations
 * encaissent déjà les bloquerait sans préavis — d'où le compteur « bloquantes ».
 */

interface VatRow {
  organization_id: string;
  organization_name: string;
  country: string | null;
  vat_status: string | null;
  vat_number: string | null;
  vat_declared_at: string | null;
  is_declared: boolean;
  contact_email: string | null;
  contact_name: string | null;
  events_count: number;
  has_paid_registrations: boolean;
  terms_version: string | null;
  terms_accepted_at: string | null;
  terms_accepted: boolean;
  identity_complete: boolean;
  legal_name: string | null;
  company_number: string | null;
}

// 0801403607 → 0801.403.607, le format de la BCE.
const formatCompanyNumber = (n: string) =>
  /^\d{10}$/.test(n) ? `${n.slice(0, 4)}.${n.slice(4, 7)}.${n.slice(7)}` : n;

interface VatCampaign {
  total: number;
  declared: number;
  pending: number;
  blocking: number;
  terms_current_version: string;
  terms_accepted: number;
  terms_blocking: number;
  organizations: VatRow[];
}

const AdminVatRegimes: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<VatCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get('/admin/vat-regimes');
      setData(res.data as VatCampaign);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('vatCampaign.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const statusLabel = (row: VatRow) =>
    row.vat_status ? t(`vatCampaign.statuses.${row.vat_status}`) : '—';

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Receipt className="h-5 w-5 text-muted-foreground" />
          <div>
            <h1 className="text-xl font-semibold md:text-2xl">{t('vatCampaign.title')}</h1>
            <p className="text-xs text-muted-foreground md:text-sm">
              {t('vatCampaign.subtitle')}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && !data && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
            {[
              { label: t('vatCampaign.total'), value: data.total },
              { label: t('vatCampaign.declared'), value: data.declared },
              { label: t('vatCampaign.pending'), value: data.pending },
              {
                label: t('vatCampaign.termsAccepted', {
                  version: formatTermsVersion(data.terms_current_version, i18n.language),
                }),
                value: data.terms_accepted,
              },
            ].map((s) => (
              <Card key={s.label}>
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                  <p className="text-2xl font-semibold tabular-nums">{s.value}</p>
                </CardContent>
              </Card>
            ))}
            <Card className={data.blocking > 0 ? 'border-warning/50' : undefined}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t('vatCampaign.blocking')}</p>
                <p
                  className={`text-2xl font-semibold tabular-nums ${
                    data.blocking > 0 ? 'text-warning' : ''
                  }`}
                >
                  {data.blocking}
                </p>
              </CardContent>
            </Card>
            <Card className={data.terms_blocking > 0 ? 'border-warning/50' : undefined}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">{t('vatCampaign.termsBlocking')}</p>
                <p
                  className={`text-2xl font-semibold tabular-nums ${
                    data.terms_blocking > 0 ? 'text-warning' : ''
                  }`}
                >
                  {data.terms_blocking}
                </p>
              </CardContent>
            </Card>
          </div>

          <Alert variant={data.blocking > 0 ? 'destructive' : 'default'}>
            {data.blocking > 0 && <AlertTriangle className="h-4 w-4" />}
            <AlertDescription>
              {data.blocking > 0
                ? t('vatCampaign.notReady', { count: data.blocking })
                : t('vatCampaign.ready')}
            </AlertDescription>
          </Alert>

          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-medium">{t('vatCampaign.columns.organization')}</th>
                    <th className="px-4 py-3 font-medium">{t('vatCampaign.columns.contact')}</th>
                    <th className="px-4 py-3 font-medium">{t('vatCampaign.columns.regime')}</th>
                    <th className="px-4 py-3 font-medium">{t('vatCampaign.columns.vatNumber')}</th>
                    <th className="px-4 py-3 text-right font-medium">
                      {t('vatCampaign.columns.events')}
                    </th>
                    <th className="px-4 py-3 font-medium">{t('vatCampaign.columns.paid')}</th>
                    <th className="px-4 py-3 font-medium">{t('vatCampaign.columns.identity')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.organizations.map((row) => (
                    <tr key={row.organization_id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        <div className="font-medium">{row.organization_name}</div>
                        {row.legal_name && row.legal_name !== row.organization_name && (
                          <div className="text-xs text-muted-foreground">{row.legal_name}</div>
                        )}
                        {/* Le mandat ne se signale que s'il manque : sans lui, aucun
                            relevé ne peut être émis au nom de l'organisation. */}
                        {!row.terms_accepted && (
                          <Badge
                            variant="outline"
                            title={row.terms_accepted_at || undefined}
                            className={`mt-1 ${
                              row.has_paid_registrations ? 'border-warning/50 text-warning' : ''
                            }`}
                          >
                            {row.terms_version
                              ? t('vatCampaign.termsOutdated', {
                                  version: formatTermsVersion(row.terms_version, i18n.language),
                                })
                              : t('vatCampaign.termsMissing')}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {row.contact_email ? (
                          <a className="hover:underline" href={`mailto:${row.contact_email}`}>
                            {row.contact_name || row.contact_email}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {row.is_declared ? (
                          <Badge variant="secondary">{statusLabel(row)}</Badge>
                        ) : (
                          <Badge variant="outline" className="border-warning/50 text-warning">
                            {t('vatCampaign.missing')}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground tabular-nums">
                        {row.vat_number ? (
                          row.vat_number
                        ) : row.company_number ? (
                          <span>
                            {formatCompanyNumber(row.company_number)}
                            <span className="ml-1.5 text-xs uppercase tracking-wide">
                              {t('vatCampaign.companyNumberTag')}
                            </span>
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{row.events_count}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {row.has_paid_registrations ? t('common.yes') : t('common.no')}
                      </td>
                      <td className="px-4 py-3">
                        {row.identity_complete ? (
                          <Badge variant="secondary">{t('vatCampaign.identityComplete')}</Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className={
                              row.has_paid_registrations
                                ? 'border-warning/50 text-warning'
                                : undefined
                            }
                          >
                            {t('vatCampaign.identityMissing')}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminVatRegimes;
